import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, RefreshCw, ShieldAlert, Wifi, X } from "lucide-react";
import api from "@/lib/api";

const formatTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

export default function SyncOverview({ canView }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState({ devices: [], current_version: "" });
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/presence/sync", {
        headers: { "Cache-Control": "no-cache" },
        params: { fresh: Date.now() },
      });
      setOverview(response.data || { devices: [], current_version: "" });
      setError("");
    } catch (requestError) {
      setError("Het synchronisatie-overzicht kon niet worden geladen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    load();
    const timer = window.setInterval(load, 10000);
    return () => window.clearInterval(timer);
  }, [open]);

  if (!canView) return null;

  const devices = overview.devices || [];
  const warnings = devices.filter((device) => !device.healthy).length;

  return (
    <>
      <button
        type="button"
        data-testid="sync-overview-open"
        onClick={() => setOpen(true)}
        className="mb-2 flex w-full items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-left text-xs font-semibold text-emerald-800 transition-colors hover:bg-emerald-100"
      >
        <Wifi className="h-4 w-4" />
        Synchronisatie-overzicht
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/45 p-4"
          data-testid="sync-overview-modal"
        >
          <section className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-lg bg-white shadow-2xl">
            <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">
                  Live cloudcontrole
                </p>
                <h2 className="mt-1 text-xl font-semibold text-slate-950">
                  Apparaten en synchronisatie
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Alleen verse apparaten zonder wachtrij mogen als volledig gesynchroniseerd gelden.
                </p>
              </div>
              <button
                type="button"
                data-testid="sync-overview-close"
                aria-label="Sluiten"
                onClick={() => setOpen(false)}
                className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-6 py-3">
              <div data-testid="sync-overview-summary" className="text-sm text-slate-700">
                <b>{devices.length}</b> actief · {warnings === 0 ? "alles bevestigd" : `${warnings} controle nodig`}
              </div>
              <button
                type="button"
                data-testid="sync-overview-refresh"
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Nu controleren
              </button>
            </div>

            {error ? (
              <p data-testid="sync-overview-error" className="m-6 rounded-md bg-rose-50 p-4 text-sm text-rose-800">
                {error}
              </p>
            ) : devices.length === 0 ? (
              <p data-testid="sync-overview-empty" className="p-6 text-sm text-slate-500">
                Er zijn op dit moment geen actieve apparaten gemeld.
              </p>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="sync-overview-devices">
                {devices.map((device) => (
                  <article
                    key={device.id}
                    data-testid={`sync-device-${device.id}`}
                    className="flex flex-wrap items-center justify-between gap-4 px-6 py-4"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">
                        {device.device_name}
                        <span className="ml-2 text-sm font-normal text-slate-500">· {device.name}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {device.source === "mobile" ? "Tablet/mobiel" : "Desktop"}
                        {` · laatst gemeld ${formatTime(device.last_seen)}`}
                        {device.pending_count > 0 ? ` · ${device.pending_count} wachtend` : ""}
                      </div>
                    </div>
                    <div
                      data-testid={`sync-device-status-${device.id}`}
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${
                        device.healthy
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-900"
                      }`}
                    >
                      {device.healthy ? <CheckCircle2 className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
                      {device.healthy ? "Gesynchroniseerd" : "Controle nodig"}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      , document.body)}
    </>
  );
}